/**
 * Custom options (native replacement for SC/Bold Product Options).
 *
 * Companion of snippets/product__custom-options.liquid. Handles conditional
 * visibility, required-field validation, the add-on running total, and
 * exposes the selected priced add-ons so z__jsAjaxCart.js can multi-add
 * them to the cart alongside the parent product.
 *
 * All listeners are delegated on `document`, so dynamically injected forms
 * (featured product embeds, drawer refreshes) need no re-init.
 */
(() => {
  if (Shopify.theme.customOptions) return;

  const formatMoney = (cents, format) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const template = format || '${{amount}}';
    return template.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  };

  const fieldInput = (wrapper) => wrapper.querySelector('input, select, textarea');

  const fieldValue = (wrapper) => {
    const input = fieldInput(wrapper);
    if (!input) return '';
    if (input.type === 'checkbox') return input.checked ? 'checked' : '';
    return input.value;
  };

  const isActive = (wrapper) => !wrapper.hidden;

  const setFieldError = (wrapper, hasError) => {
    wrapper.classList.toggle('custom-options__field--error', hasError);
    const error = wrapper.querySelector('[data-custom-option-error]');
    if (error) error.hidden = !hasError;
  };

  const updateConditionals = (container) => {
    const wrappers = container.querySelectorAll('[data-custom-option][data-condition-field]');

    wrappers.forEach((wrapper) => {
      const controller = container.querySelector(
        `[data-custom-option][data-option-id="${wrapper.dataset.conditionField}"]`
      );
      const matches = controller
        && isActive(controller)
        && fieldValue(controller) === wrapper.dataset.conditionValue;

      wrapper.hidden = !matches;
      wrapper.querySelectorAll('input, select, textarea').forEach((input) => {
        // Disabled inputs are excluded from FormData, so a hidden field
        // never submits a property.
        input.disabled = !matches;
      });
      if (!matches) setFieldError(wrapper, false);
    });
  };

  const selectedAddons = (container) => {
    const addons = [];

    container.querySelectorAll('[data-addon-select]').forEach((select) => {
      if (select.disabled) return;
      const option = select.selectedOptions[0];
      if (option?.dataset.addonVariantId) {
        addons.push({
          id: Number(option.dataset.addonVariantId),
          price: Number(option.dataset.addonPrice) || 0,
        });
      }
    });

    container.querySelectorAll('[data-addon-checkbox]:checked').forEach((checkbox) => {
      if (checkbox.disabled) return;
      addons.push({
        id: Number(checkbox.dataset.addonVariantId),
        price: Number(checkbox.dataset.addonPrice) || 0,
      });
    });

    return addons;
  };

  const updateAddonTotal = (container) => {
    const totalRow = container.querySelector('[data-addon-total]');
    if (!totalRow) return;

    const total = selectedAddons(container).reduce((sum, addon) => sum + addon.price, 0);

    totalRow.hidden = total === 0;
    if (total > 0) {
      totalRow.querySelector('[data-addon-total-amount]').textContent =
        formatMoney(total, container.dataset.moneyFormat);
    }
  };

  const refresh = (container) => {
    updateConditionals(container);
    updateAddonTotal(container);
  };

  function handleChange(event) {
    const container = event.target.closest('[data-custom-options]');
    if (!container) return;
    refresh(container);

    const wrapper = event.target.closest('[data-custom-option]');
    if (wrapper) setFieldError(wrapper, false);
  }

  function handleInput(event) {
    const wrapper = event.target.closest('[data-custom-option]');
    if (!wrapper) return;

    const counter = wrapper.querySelector('[data-char-counter]');
    if (counter) {
      counter.textContent = `${event.target.value.length} | ${counter.dataset.maxLength}`;
    }
  }

  function handleReady() {
    document.querySelectorAll('[data-custom-options]').forEach(refresh);
  }

  function handleVariantChange(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    const { variant } = event.detail;

    form.querySelectorAll('[data-variant-option-property]').forEach((input) => {
      const value = variant ? variant.options[Number(input.dataset.optionIndex)] : '';
      input.value = value || '';
      // Disabled inputs are excluded from FormData, so no blank variant
      // property submits while nothing is selected.
      input.disabled = !variant;
    });
  }

  document.addEventListener('change', handleChange);
  document.addEventListener('input', handleInput);
  // Capture phase: variant-change is dispatched without bubbles (app.js).
  document.addEventListener('variant-change', handleVariantChange, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleReady);
  } else {
    handleReady();
  }

  Shopify.theme.customOptions = {
    /**
     * Validates required fields. Returns { valid, message } and toggles the
     * per-field inline error states.
     */
    validate(form) {
      let firstError = null;

      form.querySelectorAll('[data-custom-option][data-required]').forEach((wrapper) => {
        if (!isActive(wrapper)) return;

        const input = fieldInput(wrapper);
        if (!input || input.disabled) return;

        let filled;
        if (input.type === 'checkbox') {
          filled = input.checked;
        } else {
          filled = input.value.trim() !== '';
        }

        setFieldError(wrapper, !filled);
        if (!filled && !firstError) firstError = wrapper;
      });

      if (!firstError) return { valid: true, message: '' };

      const error = firstError.querySelector('[data-custom-option-error]');
      return {
        valid: false,
        message: error ? error.textContent.trim() : 'Please complete the required options',
      };
    },

    /**
     * Selected priced add-ons as cart items: [{ id: <variant id> }].
     */
    getAddonItems(form) {
      const container = form.querySelector('[data-custom-options]');
      if (!container) return [];
      return selectedAddons(container).map((addon) => ({ id: addon.id }));
    },

    /**
     * Removes empty custom-option properties from an outgoing FormData so
     * blank values (untouched optional fields) never reach the line item.
     */
    cleanFormData(formData, form) {
      form.querySelectorAll('[data-custom-option]').forEach((wrapper) => {
        const input = fieldInput(wrapper);
        if (!input || !input.name) return;

        const value = formData.get(input.name);
        if (value !== null && String(value).trim() === '') {
          formData.delete(input.name);
        }
      });
      return formData;
    },
  };
})();
